import React, { useEffect, useState } from "react";
import { api, UGX } from "../lib/api";
import { PageHeader, SectionCard, Chip, EmptyState } from "../components/Common";
import { DollarSign, Plus, Edit3, Trash2, X, Search } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = ["Laboratory", "Pharmacy", "Imaging", "Procedure", "Service"];
const TONE = { Laboratory: "green", Pharmacy: "amber", Imaging: "red", Procedure: "blue", Service: "slate" };

export default function PriceList() {
  const [rows, setRows] = useState([]);
  const [category, setCategory] = useState("all");
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null); // {} = new, {...row} = edit

  const load = async () => {
    const url = category === "all" ? "/price-list" : `/price-list?category=${encodeURIComponent(category)}`;
    const { data } = await api.get(url);
    setRows(data.items || []);
  };
  useEffect(() => { load(); }, [category]);

  const filtered = q ? rows.filter((r) => r.name.toLowerCase().includes(q.toLowerCase()) || (r.aliases || []).some((a) => a.toLowerCase().includes(q.toLowerCase()))) : rows;

  const remove = async (r) => {
    if (!window.confirm(`Delete ${r.name}? Existing invoices are unaffected.`)) return;
    try { await api.delete(`/price-list/${r.price_id}`); toast.success("Deleted"); load(); } catch { toast.error("Delete failed"); }
  };

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="price-list-page">
      <PageHeader
        title="Price list · fee schedule"
        subtitle="Single source of truth for every billable item. All cost breakdowns and invoices pull from here."
        actions={<button data-testid="price-add-btn" onClick={() => setEditing({})} className="h-10 px-4 rounded-lg bg-[#1976D2] hover:bg-[#1565C0] text-white text-sm font-semibold flex items-center gap-1.5"><Plus className="w-4 h-4" /> Add price</button>}
      />

      <SectionCard
        title="Fee schedule"
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative">
              <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
              <input data-testid="price-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="h-8 pl-8 pr-3 rounded-md border border-slate-200 text-xs w-52" />
            </div>
            <div className="flex items-center gap-1 border border-slate-200 rounded-lg p-0.5">
              {["all", ...CATEGORIES].map((c) => (
                <button key={c} data-testid={`price-cat-${c}`} onClick={() => setCategory(c)} className={`px-2 h-7 text-xs font-medium rounded-md ${category === c ? "bg-[#1976D2] text-white" : "text-slate-600 hover:bg-slate-100"}`}>{c === "all" ? "All" : c}</button>
              ))}
            </div>
          </div>
        }
      >
        {filtered.length === 0 ? (
          <EmptyState icon={DollarSign} title="No prices yet" description="Add prices via the button above — every rate shown in the app will pull from this list." />
        ) : (
          <table className="w-full text-sm" data-testid="price-table">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-4 py-2.5">Item</th>
                <th className="text-left px-4 py-2.5">Category</th>
                <th className="text-left px-4 py-2.5">Aliases</th>
                <th className="text-right px-4 py-2.5">Unit price</th>
                <th className="text-left px-4 py-2.5">Source · updated</th>
                <th className="text-right px-4 py-2.5">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((r) => (
                <tr key={r.price_id} data-testid={`price-row-${r.price_id}`}>
                  <td className="px-4 py-2.5 font-medium">{r.name}</td>
                  <td className="px-4 py-2.5"><Chip tone={TONE[r.category] || "slate"}>{r.category}</Chip></td>
                  <td className="px-4 py-2.5 text-xs text-slate-500">{(r.aliases || []).join(", ") || "—"}</td>
                  <td className="px-4 py-2.5 text-right tabular font-semibold">{UGX(r.unit_price)}</td>
                  <td className="px-4 py-2.5 text-xs">
                    <div>{r.source || "Custom"}</div>
                    <div className="text-slate-400 tabular">{r.updated_at ? `${new Date(r.updated_at).toLocaleDateString()} · ${r.updated_by_name || "—"}` : "—"}</div>
                  </td>
                  <td className="px-4 py-2.5 text-right whitespace-nowrap">
                    <button data-testid={`price-edit-${r.price_id}`} onClick={() => setEditing(r)} className="text-xs text-[#1565C0] font-medium hover:underline mr-3 inline-flex items-center gap-1"><Edit3 className="w-3 h-3" /> Edit</button>
                    <button data-testid={`price-del-${r.price_id}`} onClick={() => remove(r)} className="text-xs text-[#B71C1C] font-medium hover:underline inline-flex items-center gap-1"><Trash2 className="w-3 h-3" /> Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </SectionCard>

      {editing && <PriceEditModal row={editing} onClose={() => setEditing(null)} onDone={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function PriceEditModal({ row, onClose, onDone }) {
  const isNew = !row.price_id;
  const [f, setF] = useState({
    category: row.category || "Laboratory",
    name: row.name || "",
    aliases: (row.aliases || []).join(", "),
    unit_price: row.unit_price ?? "",
    notes: row.notes || "",
  });
  const submit = async () => {
    if (!f.name.trim()) return toast.error("Name is required");
    const price = Number(f.unit_price);
    if (isNaN(price) || price < 0) return toast.error("Enter a valid price");
    const payload = {
      category: f.category,
      name: f.name.trim(),
      aliases: f.aliases.split(",").map((s) => s.trim()).filter(Boolean),
      unit_price: price,
      notes: f.notes.trim() || null,
    };
    try {
      if (isNew) await api.post("/price-list", payload);
      else       await api.patch(`/price-list/${row.price_id}`, payload);
      toast.success(isNew ? "Price added" : "Price updated");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  return (
    <div className="fixed inset-0 z-40 bg-slate-900/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl border border-slate-200 max-w-md w-full p-6 shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="price-modal">
        <div className="flex items-center justify-between mb-4"><div className="font-semibold">{isNew ? "Add price" : "Edit price"}</div><button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button></div>
        <div className="space-y-3">
          <label className="block"><span className="text-[11px] uppercase text-slate-500 font-semibold">Category *</span>
            <select data-testid="price-cat-input" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white">
              {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
            </select>
          </label>
          <label className="block"><span className="text-[11px] uppercase text-slate-500 font-semibold">Item name *</span>
            <input data-testid="price-name-input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" placeholder="e.g. HbA1c" />
          </label>
          <label className="block"><span className="text-[11px] uppercase text-slate-500 font-semibold">Aliases (comma-separated)</span>
            <input data-testid="price-aliases-input" value={f.aliases} onChange={(e) => setF({ ...f, aliases: e.target.value })} className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" placeholder="A1c, glycated" />
          </label>
          <label className="block"><span className="text-[11px] uppercase text-slate-500 font-semibold">Unit price (UGX) *</span>
            <input data-testid="price-price-input" type="number" min={0} step={100} value={f.unit_price} onChange={(e) => setF({ ...f, unit_price: e.target.value })} className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm tabular text-right" />
          </label>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="h-10 px-4 rounded-lg border border-slate-300 text-sm font-medium">Cancel</button>
          <button data-testid="price-save-btn" onClick={submit} className="h-10 px-5 rounded-lg bg-[#2E7D32] hover:bg-[#1B5E20] text-white text-sm font-semibold">{isNew ? "Add to fee schedule" : "Save changes"}</button>
        </div>
      </div>
    </div>
  );
}
